import { useCallback } from 'react';
import { setSessionVar, handleError } from '../../../lib/helpers';

export const useDialogsLogic = ({
    notifyError,
    notifySuccess,
    userOrg,
    getSourcesByIds,
    handleMigrationReadinessDialogClose,
    setMigrationReadinessDialogOpen,
    setSelectedSourcesForMigration,
    setPreflightRequests,
    setPreflightModalConfig,
    setPreflightModalOpen,
    selectedSourcesForInspection,
    handleSourceInspectionDialogClose,
    selectedSourcesForInSitu,
    handleInSituDialogClose,
}) => {
    const handleStartMigration = useCallback(
        (sourceId) => {
            const sources = sourceId ? [sourceId] : [];
            const selectedSources = getSourcesByIds(sources);
            setSelectedSourcesForMigration(selectedSources);
            setMigrationReadinessDialogOpen(true);
        },
        [getSourcesByIds, setSelectedSourcesForMigration, setMigrationReadinessDialogOpen],
    );

    const handleMigrationReadinessConfirm = useCallback(
        async (migrationData) => {
            try {
                const preflightPayload = migrationData.selectedSources.map((source) => {
                    const overrides = {};
                    if (migrationData.ignoreValidationErrors) {
                        overrides.ignore_validation_errors = true;
                    }
                    if (migrationData.useStoredData && source.attribute_state === 'success') {
                        overrides.exclude = ['source_attributes_check'];
                    }
                    return {
                        resource_id: source.id,
                        type: 'source',
                        overrides,
                        sourceData: {
                            id: source.id,
                            host: source.host,
                            hostname: source.hostname,
                            name: source.host || source.hostname || source.id,
                            os_type: source.os_type,
                        },
                    };
                });

                if (migrationData.cloudAccount) {
                    preflightPayload.push({
                        resource_id: migrationData.cloudAccount.id,
                        cloud_type: migrationData.cloudAccount.cloud_type,
                        type: 'target_cloud',
                        migrate_netapp_files: false,
                        sourceData: {
                            id: migrationData.cloudAccount.id,
                            name: migrationData.cloudAccount.name || 'Target Cloud',
                            cloud_type: migrationData.cloudAccount.cloud_type,
                        },
                    });
                }

                const isMigrateNetAppFiles = migrationData.selectedSources.some(
                    (source) => source.migrate_netapp_files,
                );
                const isVdiMigration = migrationData.migrationType === 'vdi';
                const dataOnlyMigration =
                    isMigrateNetAppFiles ||
                    migrationData.migrationType === 'dataOnlyMigration' ||
                    migrationData.migrationType === 'vdi' ||
                    (migrationData.migrationType === 'file' && migrationData.fileMobilityType === 'dataOnlyMigration');

                const migrationContext = {
                    applianceId: migrationData.cloudAccount?.appliance?.id,
                    targetAccount: migrationData.cloudAccount?.id,
                    cloudAccount: migrationData.cloudAccount,
                    migrationType: migrationData.migrationType,
                    dataOnlyMigration,
                    sourceCredentials: {},
                    migrate_netapp_files: isMigrateNetAppFiles,
                    vdi_migration: isVdiMigration,
                    block_mobility_target_type: migrationData.blockMobilityType || 'existing_disk_on_host',
                    ignoreValidationErrors: migrationData.ignoreValidationErrors || false,
                    in_situ: false,
                };

                if (migrationData.credentialSets) {
                    Object.entries(migrationData.credentialSets).forEach(([sourceId, credentialTag]) => {
                        migrationContext.sourceCredentials[sourceId] = {
                            storage: 'ca',
                            tag: credentialTag,
                        };
                    });
                }

                handleMigrationReadinessDialogClose();

                setPreflightRequests(preflightPayload);
                setPreflightModalConfig({
                    title: 'Migration Readiness Check',
                    loadingMessage: 'Initiating Migration Preflight...',
                    proceedOnSuccess: false,
                    type: 'migration',
                    migrationContext,
                });
                setPreflightModalOpen(true);
            } catch (error) {
                handleError(error, notifyError, 'Failed to initiate migration readiness check');
            }
        },
        [
            notifyError,
            handleMigrationReadinessDialogClose,
            setPreflightRequests,
            setPreflightModalConfig,
            setPreflightModalOpen,
        ],
    );

    const handleSourceInspectionDialogConfirm = useCallback(
        async (inspectionData) => {
            try {
                const preflightPayload = selectedSourcesForInspection.map((source) => {
                    const overrides = {};
                    if (inspectionData.useStoredData && source.attribute_state === 'success') {
                        overrides.exclude = ['source_attributes_check'];
                    }
                    return {
                        resource_id: source.id,
                        type: 'source_discovery',
                        ...(Object.keys(overrides).length > 0 && { overrides }),
                        organization_id: userOrg,
                        validation_attributes: {
                            target_cloud: inspectionData.cloudAccount.id,
                            use_stored_data: inspectionData.useStoredData,
                        },
                        sourceData: {
                            id: source.id,
                            host: source.host,
                            hostname: source.hostname,
                            name: source.host || source.hostname || source.id,
                            os_type: source.os_type,
                        },
                    };
                });

                if (inspectionData.cloudAccount) {
                    preflightPayload.push({
                        resource_id: inspectionData.cloudAccount.id,
                        cloud_type: inspectionData.cloudAccount.cloud_type,
                        type: 'target_cloud',
                        migrate_netapp_files: false,
                        sourceData: {
                            id: inspectionData.cloudAccount.id,
                            name: inspectionData.cloudAccount.name || 'Target Cloud',
                            cloud_type: inspectionData.cloudAccount.cloud_type,
                        },
                    });
                }

                setSessionVar('selectedModels', selectedSourcesForInspection);
                setSessionVar('preflight_payload', preflightPayload);

                const migrationContext = {
                    cloudAccount: inspectionData.cloudAccount,
                    targetAccount: inspectionData.cloudAccount.id,
                    appliance_id: inspectionData.cloudAccount.appliance_id,
                    cloud_type: inspectionData.cloudAccount.cloud_type,
                    useStoredData: inspectionData.useStoredData,
                    dataOnlyMigration: false,
                    migrate_netapp_files: false,
                    vdi_migration: false,
                    in_situ: false,
                    ...(Object.keys(inspectionData.credentialSets || {}).length > 0 && {
                        credentialSets: inspectionData.credentialSets,
                    }),
                };
                if (inspectionData.credentialSets) {
                    migrationContext.sourceCredentials = {};
                    Object.entries(inspectionData.credentialSets).forEach(([sourceId, credentialTag]) => {
                        migrationContext.sourceCredentials[sourceId] = {
                            storage: 'ca',
                            tag: credentialTag,
                        };
                    });
                }
                setSessionVar('sourceInspectionContext', migrationContext);

                handleSourceInspectionDialogClose();

                setTimeout(() => {
                    if (window.reactNavigate) {
                        window.reactNavigate('/source_inspection');
                    } else {
                        window.location.href = '/source_inspection';
                    }
                }, 100);

                notifySuccess({ description: 'Initiating source inspection...' });
            } catch (error) {
                handleError(error, notifyError, 'Failed to initiate source inspection');
            }
        },
        [selectedSourcesForInspection, userOrg, handleSourceInspectionDialogClose, notifySuccess, notifyError],
    );

    const handleInSituConfirm = useCallback(
        async (migrationData) => {
            try {
                const preflightPayload = migrationData.selectedSources.map((source) => {
                    const overrides = {};
                    if (migrationData.useStoredData && source.attribute_state === 'success') {
                        overrides.exclude = ['source_attributes_check'];
                    }
                    return {
                        resource_id: source.id,
                        type: 'in_situ_modernization',
                        ...(Object.keys(overrides).length > 0 && { overrides }),
                        sourceData: {
                            id: source.id,
                            host: source.host,
                            hostname: source.hostname,
                            name: source.host || source.hostname || source.id,
                            os_type: source.os_type,
                        },
                    };
                });

                if (migrationData.cloudAccount) {
                    preflightPayload.push({
                        resource_id: migrationData.cloudAccount.id,
                        cloud_type: migrationData.cloudAccount.cloud_type,
                        type: 'target_cloud',
                        migrate_netapp_files: false,
                        sourceData: {
                            id: migrationData.cloudAccount.id,
                            name: migrationData.cloudAccount.name || 'Target Cloud',
                            cloud_type: migrationData.cloudAccount.cloud_type,
                        },
                    });
                }

                handleInSituDialogClose?.();

                setPreflightRequests(preflightPayload);
                setPreflightModalConfig({
                    title: 'In-Situ Upgrade Readiness',
                    loadingMessage: 'Initiating In-Situ Preflight...',
                    proceedOnSuccess: false,
                    type: 'migration',
                    migrationContext: {
                        cloudAccount: migrationData.cloudAccount,
                        targetAccount: migrationData.cloudAccount?.id,
                        in_situ: true,
                    },
                });
                setPreflightModalOpen(true);
            } catch (error) {
                handleError(error, notifyError, 'Failed to initiate in-situ readiness check');
            }
        },
        [notifyError, handleInSituDialogClose, setPreflightRequests, setPreflightModalConfig, setPreflightModalOpen],
    );

    return {
        handleStartMigration,
        handleMigrationReadinessConfirm,
        handleSourceInspectionDialogConfirm,
        handleInSituConfirm,
    };
};
